import pino from "pino";

let sharedLogger: pino.Logger | undefined;

export type LogLevel = "debug" | "info" | "warn" | "error";

const pinoLogger = () => {
	if (sharedLogger) {
		return sharedLogger;
	}

	sharedLogger = pino({
		base: null,
		level: "info",
		formatters: {
			level(label) {
				return { level: label.toUpperCase() };
			},
		},
	});

	return sharedLogger;
};

const VALID_LEVELS = new Set<LogLevel>(["debug", "info", "warn", "error"]);

export const initLogger = ({ level }: { level: string }): void => {
	const normalized = level.toLowerCase() as LogLevel;
	if (!VALID_LEVELS.has(normalized)) {
		throw new Error(`Invalid log level: "${level}". Must be one of: ${[...VALID_LEVELS].join(", ")}`);
	}
	const logger = pinoLogger();
	logger.level = normalized;
};

export type Logger = {
	info: (message: string, data?: Record<string, unknown>) => void;
	warn: (message: string, data?: Record<string, unknown>) => void;
	debug: (message: string, data?: Record<string, unknown>) => void;
	error: (message: string, err: Error, data?: { logGroup: string } & Record<string, unknown>) => void;
};

export const createContextId = (): string => {
	const bytes = new Uint8Array(16);
	crypto.getRandomValues(bytes);

	// Set version (4) and variant (RFC 4122) bits
	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;

	const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
	return `${hex[0]}${hex[1]}${hex[2]}${hex[3]}-${hex[4]}${hex[5]}-${hex[6]}${hex[7]}-${hex[8]}${hex[9]}-${hex[10]}${hex[11]}${hex[12]}${hex[13]}${hex[14]}${hex[15]}`;
};

export type CreateLoggerOptions = {
	service: string;
	contextId?: string;
	level?: LogLevel;
};

export const createLogger = ({ service, contextId, level }: CreateLoggerOptions): Logger => {
	const baseLogger = pinoLogger();
	const logger = level && level !== baseLogger.level ? baseLogger.child({}, { level }) : baseLogger;
	const getContextId = () => contextId || createContextId();
	return {
		info: (message: string, data: Record<string, unknown> = {}) => {
			logger.info({ service, contextId: getContextId(), ...data }, message);
		},
		warn: (message: string, data: Record<string, unknown> = {}) => {
			logger.warn({ service, contextId: getContextId(), ...data }, message);
		},
		debug: (message: string, data: Record<string, unknown> = {}) => {
			logger.debug({ service, contextId: getContextId(), ...data }, message);
		},
		error: (
			message: string,
			err: Error,
			data: { logGroup: string } & Record<string, unknown> = { logGroup: "DEFAULT" },
		) => {
			logger.error({ service, contextId: getContextId(), ...data, err }, message);
		},
	};
};
